import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, User, Users, CheckCircle, XCircle, MessageSquare, Image, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

interface ObservationsViewProps {
  companyId: string;
}

interface Observation {
  id: string;
  worker_id: string;
  completed_at: string;
  response_data: {
    observee_id?: string;
    observee_name?: string;
    checklist?: Array<{ item: string; checked: boolean }>;
    notes?: string;
    photos?: string[];
    feedback_given?: boolean;
  };
  worker_profiles: {
    display_name: string;
    team: string;
  };
}

export default function ObservationsView({ companyId }: ObservationsViewProps) {
  const [observations, setObservations] = useState<Observation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadObservations();
  }, [companyId]);

  const loadObservations = async () => {
    try {
      // Get quest templates for peer_observation
      const { data: templates } = await supabase
        .from('quest_templates')
        .select('id')
        .eq('company_id', companyId)
        .eq('intervention_type', 'peer_observation');

      if (!templates?.length) {
        setLoading(false);
        return;
      }

      const templateIds = templates.map(t => t.id);

      // Get completed observation quests
      const { data, error } = await supabase
        .from('worker_quests')
        .select(`
          id,
          worker_id,
          completed_at,
          response_data,
          worker_profiles!inner(display_name, team, company_id)
        `)
        .in('template_id', templateIds)
        .eq('status', 'completed')
        .eq('worker_profiles.company_id', companyId)
        .order('completed_at', { ascending: false });

      if (error) throw error;
      setObservations((data || []) as unknown as Observation[]);
    } catch (error) {
      console.error('Error loading observations:', error);
      toast.error('Failed to load observations');
    } finally {
      setLoading(false);
    }
  };

  const getChecklistStats = (checklist?: Array<{ item: string; checked: boolean }>) => {
    if (!checklist || checklist.length === 0) return { checked: 0, total: 0, percentage: 0 };
    const checked = checklist.filter(c => c.checked).length;
    return {
      checked,
      total: checklist.length,
      percentage: Math.round((checked / checklist.length) * 100)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-indigo-500" />
            <div>
              <p className="text-2xl font-bold">{observations.length}</p>
              <p className="text-sm text-muted-foreground">Total Observations</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-2xl font-bold">
                {observations.filter(o => o.response_data.feedback_given).length}
              </p>
              <p className="text-sm text-muted-foreground">Feedback Given</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Observations List */}
      <div className="space-y-3">
        {observations.length === 0 ? (
          <Card className="p-8 text-center">
            <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No peer observations completed yet</p>
          </Card>
        ) : (
          observations.map((obs, index) => {
            const stats = getChecklistStats(obs.response_data.checklist);
            const isExpanded = expandedId === obs.id;

            return (
              <motion.div
                key={obs.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden">
                  <div 
                    className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : obs.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold">{obs.worker_profiles.display_name}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-medium text-primary">
                              {obs.response_data.observee_name || 'Unknown'}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(obs.completed_at).toLocaleDateString('id-ID', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={stats.percentage >= 80 ? 'default' : stats.percentage >= 50 ? 'secondary' : 'destructive'}
                          className={stats.percentage >= 80 ? 'bg-green-500' : ''}
                        >
                          {stats.checked}/{stats.total} ({stats.percentage}%)
                        </Badge>
                        {obs.response_data.photos && obs.response_data.photos.length > 0 && (
                          <Badge variant="outline">
                            <Image className="h-3 w-3 mr-1" />
                            {obs.response_data.photos.length}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t"
                      >
                        <div className="p-4 space-y-4">
                          {/* Checklist Results */}
                          {obs.response_data.checklist && obs.response_data.checklist.length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2">Safety Checklist</h4>
                              <div className="space-y-1">
                                {obs.response_data.checklist.map((item, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-sm">
                                    {item.checked ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <XCircle className="h-4 w-4 text-red-500" />
                                    )}
                                    <span className={item.checked ? '' : 'text-red-500'}>
                                      {item.item}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          {obs.response_data.notes && (
                            <div>
                              <h4 className="font-medium mb-2">Notes</h4>
                              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                                {obs.response_data.notes}
                              </p>
                            </div>
                          )}

                          {/* Photos */}
                          {obs.response_data.photos && obs.response_data.photos.length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2">Photos</h4>
                              <div className="flex gap-2 flex-wrap">
                                {obs.response_data.photos.map((photo, idx) => (
                                  <img 
                                    key={idx} 
                                    src={photo} 
                                    alt={`Observation photo ${idx + 1}`}
                                    className="w-24 h-24 object-cover rounded-lg"
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Feedback Status */}
                          <div className="flex items-center gap-2 text-sm">
                            <MessageSquare className="h-4 w-4" />
                            <span>
                              Feedback: {obs.response_data.feedback_given ? (
                                <Badge className="bg-green-500 ml-1">Given</Badge>
                              ) : (
                                <Badge variant="outline" className="ml-1">Not Yet</Badge>
                              )}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
